import Table, { Row } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { ColumnDef, OnChangeFn, SortingState } from "@tanstack/react-table";
import { RowType } from "@/components/Table/Table";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";

import classNames from "classnames";

interface TableDetailsPanelProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  title?: string;
  loading?: boolean;
  renderFooter?: () => React.ReactElement;
  onClickRow?: (row: Row<T>) => void;
  getRowClasses?: (row: Row<T>) => string;
  sortingState?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  renderFilteredEmptyState?: () => React.ReactElement;
  renderNoDataEmptyState?: () => React.ReactElement;
}

export default function TableDetailsPanel<T extends RowType>({
  data,
  columns,
  title,
  loading = false,
  renderFooter,
  onClickRow,
  getRowClasses,
  sortingState,
  onSortingChange,
  renderFilteredEmptyState,
  renderNoDataEmptyState
}: TableDetailsPanelProps<T>) {
  const { isCompactLayout } = useIsCompactLayout();

  return (
    <div className="border-t border-sys-brand-surface-container-highest bg-sys-brand-surface-container">
      {title && isCompactLayout && <h3 className="text-title-small px-4 py-4">{title}</h3>}
      <div
        className={classNames({
          "overflow-hidden [&_.table-mobile_.flex-1]:px-8": isCompactLayout,
          "px-12 py-8": !isCompactLayout
        })}
      >
        <Table
          data={data}
          loading={loading}
          columns={columns}
          onClickRow={onClickRow}
          getRowClasses={getRowClasses}
          sortingState={sortingState}
          onSortingChange={onSortingChange}
          useAltBorder
          {...(renderNoDataEmptyState ? { renderNoDataEmptyState } : {})}
          renderFilteredEmptyState={
            renderFilteredEmptyState ??
            (() => (
              <EmptyState
                title="No Data Found"
                caption="There are no records to display at this time"
                iconName="scan_delete"
              />
            ))
          }
          renderFooter={renderFooter}
          data-testid="table-details-panel"
        />
      </div>
    </div>
  );
}
